import React, { useState, useEffect } from 'react';
import { buildApiUrl, API_CONFIG } from '../config';
import { apiFetch } from '../utils/auth';
import { RealUserIdentity, getRealUserIdentity, setRealUserIdentity } from '../utils/realUser';

interface UserFromAPI {
  id: number;
  firstName: string;
  lastName: string;
  username?: string;
  email?: string;
  typeID?: number; // 1 = Persönlich, 2 = Technisch (Shared Device)
}

interface UserIdentificationModalProps {
  open: boolean;
  onConfirm: (identity: RealUserIdentity) => void;
  onCancel: () => void;
  /** Wenn true, wird der bestehende Benutzer zur Bestätigung angezeigt */
  confirmMode?: boolean;
  /** Optionale Ausschlussliste (Format: "vorname|nachname", lowercase) */
  excludedUserKeys?: string[];
}

const UserIdentificationModal: React.FC<UserIdentificationModalProps> = ({ open, onConfirm, onCancel, confirmMode = false, excludedUserKeys = [] }) => {
  const [users, setUsers] = useState<UserFromAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showChangeUser, setShowChangeUser] = useState(false);

  const existingIdentity = getRealUserIdentity();

  // Benutzerliste von API laden
  useEffect(() => {
    if (!open) return;
    
    const loadUsers = async () => {
      setLoading(true);
      setError('');
      
      try {
        // OrgID aus Token extrahieren
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        let orgID: number | null = null;
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
          } catch {}
        }

        // Ausgewählte Org prüfen
        const selectedOrgID = localStorage.getItem('selectedOrgID');
        if (selectedOrgID) {
          orgID = parseInt(selectedOrgID);
        }

        if (!orgID || !navigator.onLine) {
          // Offline: versuche aus localStorage zu laden
          const cached = localStorage.getItem(`activeUsers_${orgID}`);
          if (cached) {
            setUsers(JSON.parse(cached));
          } else {
            setUsers([]);
            if (!navigator.onLine) {
              setError('Offline – Benutzerliste nicht verfügbar. Bitte später erneut versuchen.');
            }
          }
          setLoading(false);
          return;
        }

        const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.USERS_ACTIVE), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgID })
        });

        if (response.ok) {
          const data: UserFromAPI[] = await response.json();
          setUsers(data);
          // Cache für Offline-Nutzung
          localStorage.setItem(`activeUsers_${orgID}`, JSON.stringify(data));
        } else {
          // Fallback auf Cache
          const cached = localStorage.getItem(`activeUsers_${orgID}`);
          if (cached) {
            setUsers(JSON.parse(cached));
          } else {
            setError('Benutzerliste konnte nicht geladen werden.');
          }
        }
      } catch (err) {
        // Fallback auf Cache
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        let orgID: number | null = null;
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
          } catch {}
        }
        const selectedOrgID = localStorage.getItem('selectedOrgID');
        if (selectedOrgID) orgID = parseInt(selectedOrgID);
        
        if (orgID) {
          const cached = localStorage.getItem(`activeUsers_${orgID}`);
          if (cached) {
            setUsers(JSON.parse(cached));
          }
        }
        setError('Fehler beim Laden der Benutzer.');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [open]);

  if (!open) return null;

  const excludedKeysSet = new Set(excludedUserKeys.map(k => k.toLowerCase()));
  if (existingIdentity?.firstName && existingIdentity?.lastName) {
    excludedKeysSet.add(`${existingIdentity.firstName.toLowerCase()}|${existingIdentity.lastName.toLowerCase()}`);
  }

  // Nur normale Benutzer anzeigen (technische Accounts mit typeID === 2 ausblenden)
  const filteredUsers = users
    .filter(u => u.typeID !== 2)
    .filter(u => !excludedKeysSet.has(`${u.firstName.toLowerCase()}|${u.lastName.toLowerCase()}`))
    .filter(u => {
      const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
      const term = searchTerm.toLowerCase();
      return fullName.includes(term) || (u.username && u.username.toLowerCase().includes(term));
    });

  const handleSelectUser = (user: UserFromAPI) => {
    const identity: RealUserIdentity = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      manual: false,
      confirmedAt: Date.now()
    };
    setRealUserIdentity(identity);
    onConfirm(identity);
  };

  const handleConfirmExisting = () => {
    if (!existingIdentity) return;
    const updated: RealUserIdentity = {
      ...existingIdentity,
      confirmedAt: Date.now()
    };
    setRealUserIdentity(updated);
    onConfirm(updated);
  };

  // Confirm-Modus: Bestehenden Benutzer bestätigen oder ändern
  if (confirmMode && existingIdentity && !showChangeUser) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, padding: 28, minWidth: 340, maxWidth: 440,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)', textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: 20 }}>Benutzer bestätigen</h3>
          <p style={{ color: '#666', fontSize: 14, margin: '0 0 20px 0' }}>
            Bitte bestätigen Sie Ihre Identität für diesen Einsatz.
          </p>
          
          <div style={{
            background: '#f5f5f5', borderRadius: 12, padding: 16, marginBottom: 20,
            border: '2px solid #c00'
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>
              {existingIdentity.firstName} {existingIdentity.lastName}
            </div>
            {existingIdentity.manual && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Manuell erfasst</div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={onCancel} style={{
              background: '#eee', border: 'none', borderRadius: 8, padding: '10px 20px',
              fontSize: 15, fontWeight: 600, cursor: 'pointer', color: '#555'
            }}>Abbrechen</button>
            <button onClick={() => setShowChangeUser(true)} style={{
              background: '#fff', border: '2px solid #c00', borderRadius: 8, padding: '10px 20px',
              fontSize: 15, fontWeight: 600, cursor: 'pointer', color: '#c00'
            }}>Ändern</button>
            <button onClick={handleConfirmExisting} style={{
              background: '#c00', border: 'none', borderRadius: 8, padding: '10px 20px',
              fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#fff'
            }}>Bestätigen</button>
          </div>
        </div>
      </div>
    );
  }

  // Benutzer-Auswahl
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28, minWidth: 340, maxWidth: 500, width: '90vw',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
      }}>
        <h3 style={{ margin: '0 0 4px 0', color: '#333', fontSize: 20, textAlign: 'center' }}>
          Wer bedient das Gerät?
        </h3>
        <p style={{ color: '#666', fontSize: 14, margin: '0 0 16px 0', textAlign: 'center' }}>
          Technischer Account – bitte identifizieren Sie sich.
        </p>

        {/* Suchfeld */}
        <input
          type="text"
          placeholder="Benutzer suchen..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 8,
            border: '1px solid #ddd', fontSize: 15, marginBottom: 12,
            boxSizing: 'border-box'
          }}
          autoFocus
        />

        {/* Benutzerliste */}
        <div style={{
          flex: 1, overflowY: 'auto', marginBottom: 12, maxHeight: '40vh',
          border: '1px solid #eee', borderRadius: 8
        }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Lade Benutzer...</div>
          ) : error && users.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#c00', fontSize: 14 }}>{error}</div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 14 }}>
              {searchTerm ? 'Kein Benutzer gefunden.' : 'Keine Benutzer verfügbar.'}
            </div>
          ) : (
            filteredUsers.map(user => (
              <div
                key={user.id}
                onClick={() => handleSelectUser(user)}
                style={{
                  padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                  display: 'flex', alignItems: 'center', gap: 10,
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: '#c00',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14, flexShrink: 0
                }}>
                  {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: '#333' }}>
                    {user.firstName} {user.lastName}
                  </div>
                  {user.username && (
                    <div style={{ fontSize: 12, color: '#999' }}>{user.username}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Button */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-start' }}>
          <button onClick={onCancel} style={{
            background: '#eee', border: 'none', borderRadius: 8, padding: '10px 16px',
            fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#555'
          }}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

export default UserIdentificationModal;

